import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useContext, useMemo, useRef } from 'react';
import { useAppSelector } from 'shared/hooks/redux/hooks';
import { AutoIgnitionState } from 'console/device/types';
import { createSubsetSelector } from 'webui/utils/selectors';
import * as S from './styles';
import { VehicleState } from './vehicle-state';
import { Box, Tooltip, Typography } from '@mui/material';
import { AcUnit, HourglassTop, Speed, Today } from '@mui/icons-material';
import { ResponsiveIcon } from 'console/shared/components/icon/responsive';
import { VehicleIndicator, VehicleThermometerIndicator, } from 'console/shared/components/indicators/vehicle';
import { CustomIcon } from 'console/shared/components/icon/custom';
import { vehicle_thermometer_definitions } from 'console/shared/content/device/thermometers/vehicle';
import { VehicleThermometerProvider } from '../../../thermometers/thermometer-card/provider/vehicle';
import { selectDevice } from 'entities/device/lib';
import { useDeviceConfig } from 'console/shared/contexts/device/config';
import { useDeviceState } from 'console/shared/contexts/device/state';
import { TabContext } from 'console/navigation/context/type';
import { GuardState } from 'console/device/device_ts';
import { useCurrentVehicleAddress } from 'console/shared/hooks/vehicle';
import { getVehicleThermometerPosition } from './models/utils';
import { getEntries } from 'console/shared/utils/entries';
import { CarModels } from 'console/shared/components/panels/vehicle-panel/components/model-view/models/types';
const car_display_device_selector = createSubsetSelector([
    'doors',
    'door_1',
    'door_2',
    'door_3',
    'door_4',
    'hood_trunk',
    'hood',
    'trunk',
    'engine_state',
    'car_view',
    'last_gps',
    'guard_state',
    'auto_ignition',
    'auto_ignition_params',
    'siren',
]);
const speed_icon_sx = { minWidth: 20 };
//Нужно для проверки, запускает всё
const all_open = false;
export const DeviceModelView = memo(function DeviceModelView(props) {
    const { car_model, center, thermometers } = props;
    const { device_id } = useDeviceConfig();
    const { online } = useDeviceState();
    const { dashboard_selected } = useContext(TabContext);
    const vehicle_ref = useRef(null);
    const device = useAppSelector((state) => car_display_device_selector(selectDevice(state, device_id)));
    const model = useMemo(() => {
        if (car_model) {
            return car_model.model;
        }
        if (device?.car_view) {
            return device?.car_view.model;
        }
        return CarModels.car;
    }, [car_model, device?.car_view]);
    const locs = useMemo(() => {
        if (device?.last_gps?.[0]) {
            const { x, y } = device.last_gps[0];
            return [x, y];
        }
        return null;
    }, [device?.last_gps?.[0]]);
    const address = useCurrentVehicleAddress(device_id, locs, 60e3);
    const speed = useMemo(() => device?.last_gps?.[0]?.speed, [device?.last_gps]);
    const arming = useMemo(() => (device?.guard_state === GuardState.disabled ? false : true), [device?.guard_state]);
    const { front_left_open, front_right_open, rear_left_open, rear_right_open, hood, trunk, smoke, is_mirrored, auto_ignition, auto_ignition_by_schedule, auto_ignition_by_temperature, auto_ignition_by_timeout, siren, } = useMemo(() => {
        if (device && !all_open) {
            const { door_1, door_2, door_3, door_4, doors, hood, hood_trunk, trunk, engine_state, car_view, auto_ignition, auto_ignition_params, siren, } = device;
            return {
                front_left_open: door_1 ?? doors,
                front_right_open: door_2 ?? doors,
                rear_left_open: door_3 ?? doors,
                rear_right_open: door_4 ?? doors,
                hood: hood ?? hood_trunk,
                trunk: trunk ?? hood_trunk,
                smoke: engine_state,
                is_mirrored: car_model ? car_model.mirrored : !!car_view?.mirrored,
                auto_ignition: auto_ignition?.state === AutoIgnitionState.enabled ?? false,
                auto_ignition_by_temperature: auto_ignition_params
                    ? auto_ignition_params['by-temperature']
                    : false,
                auto_ignition_by_schedule: auto_ignition_params
                    ? auto_ignition_params['by-schedule']
                    : false,
                auto_ignition_by_timeout: auto_ignition_params
                    ? auto_ignition_params['by-timeout']
                    : false,
                siren: siren,
            };
        }
        return {
            front_left_open: all_open,
            front_right_open: all_open,
            rear_left_open: all_open,
            rear_right_open: all_open,
            hood: all_open,
            trunk: all_open,
            smoke: all_open,
            is_mirrored: all_open,
            auto_ignition: all_open,
            auto_ignition_by_temperature: all_open,
            auto_ignition_by_schedule: all_open,
            auto_ignition_by_timeout: all_open,
            siren: all_open,
        };
    }, [all_open, device, car_model]);
    const support_model_thermometers = useMemo(() => {
        if (!model) {
            return false;
        }
        const supported_models = [
            CarModels.car,
            CarModels.hatch,
            CarModels.suv,
            CarModels.wagon,
            CarModels.pickup,
            CarModels.coupe,
            CarModels.boxVan,
            CarModels.microbus,
            CarModels.porsche,
            CarModels.gelik,
            CarModels.prado,
        ];
        return supported_models.includes(model);
    }, [model]);
    const vehicle_thermometers = useMemo(() => {
        if (thermometers) {
            return thermometers.map((thermometer_config) => {
                const { id, name: label } = thermometer_config;
                if (label === 'Датчик') {
                    return null;
                }
                if (!support_model_thermometers) {
                    return null;
                }
                const [key, value] = getEntries(vehicle_thermometer_definitions).find(([, value]) => value.label === label) ?? [];
                return (_jsx(VehicleThermometerProvider, { thermometer_config: thermometer_config, children: _jsx(VehicleThermometerIndicator, { is_mirrored: is_mirrored, model_ref: vehicle_ref, fontColor: device?.car_view?.color?.font, color: key !== 'outside' ? device?.car_view?.color?.body : null, position: getVehicleThermometerPosition(model)[key ?? 'none'], icon: value?.Icon && (_jsx(CustomIcon, { Icon: value.Icon, color: "primary", fontSize: "1.1rem" })) }) }, id));
            });
        }
        return null;
    }, [
        thermometers,
        is_mirrored,
        support_model_thermometers,
        model,
        device?.car_view?.color,
    ]);
    const vehicle_state = useMemo(() => ({
        opened_doors: front_left_open ||
            front_right_open ||
            rear_left_open ||
            rear_right_open,
        engine_working: smoke,
        hood_opened: hood,
        trunk_opened: trunk,
    }), [
        front_left_open,
        front_right_open,
        rear_left_open,
        rear_right_open,
        smoke,
        hood,
        trunk,
    ]);
    const { mirror, rotate_mirror } = useMemo(() => {
        const mirror = [
            CarModels.atv,
            CarModels.boat,
            CarModels.moto,
            CarModels.samokat,
            CarModels.scooter,
            CarModels.moto_sport_tour,
            CarModels.snow_mobile,
            CarModels.water_scooter,
            CarModels.airplane,
            CarModels.helicopter,
        ].includes(model) && is_mirrored;
        const rotate_mirror = [
            CarModels.car,
            CarModels.microbus,
            CarModels.pickup,
            CarModels.suv,
            CarModels.coupe,
            CarModels.hatch,
            CarModels.wagon,
            CarModels.boxVan,
            CarModels.gelik,
            CarModels.porsche,
            CarModels.prado,
        ].includes(model) && is_mirrored;
        return { mirror, rotate_mirror };
    }, [model, is_mirrored]);
    return (_jsxs(S.ModelView, { center: center, children: [_jsxs(Box, { position: "relative", children: [vehicle_thermometers, _jsx(S.CarModel, { model: model, frontLeftOpen: front_left_open, frontRightOpen: front_right_open, rearLeftOpen: rear_left_open, rearRightOpen: rear_right_open, online: online, hood: hood, trunk: trunk, smoke: smoke, mirror: mirror, arming: arming, rotate_mirror: rotate_mirror, auto_ignition: auto_ignition, car_color: device?.car_view?.color, siren: siren }, arming.toString())] }), _jsxs(Box, { display: "flex", columnGap: 1, alignItems: "center", mb: 0.5, children: [!online && (_jsx(Typography, { color: "error", textAlign: "center", children: "\u0423\u0441\u0442\u0440\u043E\u0439\u0441\u0442\u0432\u043E \u043D\u0435 \u043D\u0430 \u0441\u0432\u044F\u0437\u0438" })), address && online && !dashboard_selected && (_jsx(Typography, { color: "textSecondary", textAlign: "center", children: address })), online && !dashboard_selected && (_jsx(VehicleIndicator, { icon: _jsx(ResponsiveIcon, { color: "action", fontSize: "0.9em", sx: speed_icon_sx, Icon: Speed }), value: speed, unit: "\u043A\u043C/\u0447" })), online && !dashboard_selected && (_jsxs(_Fragment, { children: [auto_ignition_by_temperature && (_jsx(Tooltip, { title: 'Автозапуск по температуре включен', children: _jsx(ResponsiveIcon, { color: "action", fontSize: "0.9em", Icon: AcUnit }) })), auto_ignition_by_schedule && (_jsx(Tooltip, { title: 'Автозапуск по расписанию включен', children: _jsx(ResponsiveIcon, { color: "action", fontSize: "0.9em", Icon: Today }) })), auto_ignition_by_timeout && (_jsx(Tooltip, { title: 'Автозапуск по таймауту включен', children: _jsx(ResponsiveIcon, { color: "action", fontSize: "0.9em", Icon: HourglassTop }) }))] }))] }), !dashboard_selected && _jsx(VehicleState, { state: vehicle_state })] }));
});
